/**
 * Pre-live landing (handover §3.6 'Landing' screen / mockup
 * viewer_mobile.html screen 1): hero, ticking countdown to the event
 * start, today's stage chips from the cosmetic stages_json schedule,
 * and a "Pick a competitor" CTA that drops the fan straight into the
 * Cars list. Single column — phone-first, centered card on desktop.
 *
 * Times are the organizer's floating wall-clock verbatim (event
 * windows + StageMeta.start_time are stored tz-naive), compared
 * against the viewer's local clock — same convention as the
 * showLanding gate in BrowseStateView.
 */

import React, { useEffect } from 'react';

import { cn } from '@/lib/utils';
import { EventRow, StageMeta } from '../../services/fleet';
import { eventLogo } from './EventHeader';
import { useNowTick } from './lag';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** '2026-05-05T09:00:00' (+ optional until) → '5–7 May' / '5 May – 7 Jun'. */
const fmtDateRange = (from: string, until: string | null): string | null => {
  const parse = (s: string) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
  };
  const a = parse(from);
  if (!a) return null;
  const b = until ? parse(until) : null;
  const fmt = (p: { mo: number; d: number }) => `${p.d} ${MONTHS[p.mo - 1] ?? ''}`;
  if (!b || (b.y === a.y && b.mo === a.mo && b.d === a.d)) return fmt(a);
  if (b.y === a.y && b.mo === a.mo) return `${a.d}–${b.d} ${MONTHS[a.mo - 1] ?? ''}`;
  return `${fmt(a)} – ${fmt(b)}`;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** ms remaining → '00:46:18', days folded in front when >24 h. */
const fmtCountdown = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hh = Math.floor((total % 86_400) / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  const clock = `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
};

type ChipState = 'done' | 'next' | 'scheduled';

interface StageChip {
  label: string;
  time: string | null;
  state: ChipState;
}

/**
 * Stage chips (mockup .ss-chip row). Status is display-only schedule
 * math: on the event's start day, stages whose HH:MM has passed read
 * done (✓), the first upcoming SCHEDULED one is "next" (amber — a
 * stage with no time can't be imminent), the rest are scheduled.
 * Before the event day everything is upcoming.
 */
const buildChips = (stages: StageMeta[], now: Date, isEventDay: boolean): StageChip[] => {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const minutesOf = (t: string | null | undefined): number | null => {
    const m = (t ?? '').match(/^(\d{1,2}):(\d{2})$/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  let nextSeen = false;
  return [...stages]
    .sort((a, b) => a.index - b.index)
    .map((s) => {
      const min = minutesOf(s.start_time);
      let state: ChipState = 'scheduled';
      if (isEventDay && min != null && min < nowMin) {
        state = 'done';
      } else if (!nextSeen && min != null) {
        state = 'next';
        nextSeen = true;
      }
      return { label: `SS${s.index + 1}`, time: s.start_time ?? null, state };
    });
};

const CHIP_CLASS: Record<ChipState, string> = {
  done: 'bg-status-online/10 text-status-online',
  next: 'border border-accent/30 bg-accent/10 text-accent',
  scheduled: 'bg-bg-input text-fg-muted',
};

export const Landing: React.FC<{
  event: EventRow;
  /** 'prelive' = countdown to the event window (the original screen);
   *  'quiet' = the window is OPEN but no crew has armed a phone yet —
   *  the mid-event empty state (a live event with zero pins used to
   *  drop fans on a blank map + "no armed phones yet"). */
  variant?: 'prelive' | 'quiet';
  /** Secondary escape hatch — dismiss to the browse map. */
  onBrowse: () => void;
  /** Primary CTA — dismiss into the Cars list (mobile opens the panel). */
  onPickCompetitor: () => void;
  /** Countdown hit zero. The showLanding gate lives in the parent,
   *  which doesn't re-render on its own for a pre-live event with no
   *  armed devices — without this the timer freezes at 00:00:00. */
  onExpired: () => void;
}> = ({ event, variant = 'prelive', onBrowse, onPickCompetitor, onExpired }) => {
  const nowMs = useNowTick(1000);
  const startMs = new Date(event.active_from).getTime();
  const expired = variant === 'prelive' && startMs - nowMs <= 0;
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);
  const stages = event.stages_json?.stages ?? [];
  const now = new Date(nowMs);
  const isEventDay =
    event.active_from.slice(0, 10) ===
    `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const chips = buildChips(stages, now, isEventDay);
  const dateRange = fmtDateRange(event.active_from, event.active_until);
  const locLine = [dateRange, event.location].filter(Boolean).join(' · ');

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto bg-bg">
      <div className="flex w-full max-w-md flex-1 flex-col lg:my-10 lg:flex-none lg:overflow-hidden lg:rounded-xl lg:border lg:border-border-subtle lg:shadow-card">
        {/* Hero (mockup .m-landing-hero) */}
        <div
          className="flex h-[150px] shrink-0 flex-col justify-end p-3.5"
          style={{
            background:
              'linear-gradient(135deg, var(--accent-primary-bg) 0%, rgba(13,13,20,.9) 100%), linear-gradient(180deg, var(--bg-elevated), var(--bg-base))',
          }}
        >
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-[10px] bg-accent font-mono text-[17px] font-bold text-fg-inverse">
            {eventLogo(event.name)}
          </div>
          <div className="text-[17px] font-bold leading-tight text-fg">{event.name}</div>
          {locLine && <div className="mt-0.5 text-[11px] text-fg-secondary">{locLine}</div>}
        </div>

        {variant === 'prelive' ? (
          /* Countdown card (mockup .countdown). Target is the event
             window (active_from) — calling it a stage would lie when
             the first stage starts later than the window opens. */
          <div className="m-3.5 shrink-0 rounded-md border border-accent/30 bg-bg-surface p-3 text-center">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-accent">
              Event starts in
            </div>
            <div className="font-mono text-2xl font-bold tracking-wide text-fg">
              {fmtCountdown(startMs - nowMs)}
            </div>
            {event.timezone && (
              // Event times are organizer wall-clock; the countdown ticks
              // on the viewer's clock. Surface the zone so a remote fan
              // can tell the two apart (the v1 landing showed it too).
              <div className="mt-1 text-[9px] text-fg-muted">{event.timezone} (event local)</div>
            )}
          </div>
        ) : (
          /* Quiet card — live window, zero armed phones. Sets the
             expectation instead of a blank map: cars appear the moment
             a crew arms. */
          <div className="m-3.5 shrink-0 rounded-md border border-border-subtle bg-bg-surface p-3 text-center">
            <div className="mb-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-status-online">
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-status-online" />
              Event is live
            </div>
            <div className="text-sm font-semibold text-fg">No crews on stage yet</div>
            <div className="mx-auto mt-1 max-w-[300px] text-[11px] leading-snug text-fg-secondary">
              Phones arm shortly before each stage — cars appear here the
              moment a crew goes out. Keep this page open or check back
              around the stage times below.
            </div>
          </div>
        )}

        <div className="px-3.5 pb-5">
          {chips.length > 0 && (
            <>
              <div className="mb-2 text-[9px] font-bold uppercase tracking-wider text-fg-muted">
                {isEventDay ? "Today's stages" : 'Stages'}
              </div>
              <div className="mb-3.5 flex flex-wrap gap-1.5">
                {chips.map((c) => (
                  <span
                    key={c.label}
                    className={cn(
                      'rounded-pill px-2 py-0.5 font-mono text-[10px]',
                      CHIP_CLASS[c.state],
                    )}
                  >
                    {c.label}
                    {c.state === 'done' ? ' ✓' : c.time ? ` ${c.time}` : ''}
                  </span>
                ))}
              </div>
            </>
          )}
          {/* Quiet state has no competitors to pick — the map is the
              only destination, so it takes the primary slot. */}
          {variant === 'prelive' ? (
            <>
              <button
                type="button"
                onClick={onPickCompetitor}
                className="h-11 w-full rounded-md bg-accent text-[13px] font-bold text-fg-inverse hover:bg-accent-light"
              >
                Pick a competitor
              </button>
              <button
                type="button"
                onClick={onBrowse}
                className="mt-2.5 w-full py-1 text-center text-[11px] font-semibold text-fg-secondary hover:text-accent"
              >
                Browse the map
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onBrowse}
              className="h-11 w-full rounded-md bg-accent text-[13px] font-bold text-fg-inverse hover:bg-accent-light"
            >
              Browse the map
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Landing;
